import { Injectable } from "@angular/core";
import { environment } from "../../../environments/environment";
import { HttpClient } from "@angular/common/http";
import { timeout } from "rxjs/operators";
import { throwError, firstValueFrom } from "rxjs";
import packagejson from "../../../../package.json";
import { authConfig } from "../auth.config";

type AuthVersionsConfig = {
  citizen: { version: string };
  employee: { version: string };
};

const authVersionKey = `${packagejson.name}_auth_version`;

const authBiModeConfig = environment.authBiMode;

@Injectable({
  providedIn: "root",
})
export class AuthBiModeService {
  constructor(private httpClient: HttpClient) {}

  private getAuthVersionFromStorage(): AuthVersionsConfig {
    const authVersionConf = localStorage.getItem(authVersionKey);
    if (!authVersionConf) {
      return undefined;
    }

    try {
      return JSON.parse(authVersionConf);
    } catch (e) {
      return undefined;
    }
  }

  private setAuthVersionFromStorage(value: AuthVersionsConfig) {
    if (!value && environment.oauthParameter.showDebugInformation) {
      console.debug(
        `Attempting to setAuthVersionFromStorage with no value. Ignoring`,
      );
    }

    if (
      !navigator.cookieEnabled &&
      environment.oauthParameter.showDebugInformation
    ) {
      console.debug(
        `Attempting to setAuthVersionFromStorage while cookies are blocked. Ignoring`,
      );
    }

    if (value && navigator.cookieEnabled) {
      localStorage.setItem(authVersionKey, JSON.stringify(value));
    }
  }

  async getOAuthConfig() {
    const defaultVersion = await this.getDefaultAuthVersion();

    const authConfigOverride = this.getEffectiveAuthConfig(defaultVersion);

    const defaultAuthConfig = authConfig;
    const config = {
      ...defaultAuthConfig,
      ...authConfigOverride,
    };
    if (environment.oauthParameter.showDebugInformation) {
      console.debug(
        `Using the following to configure oauthService: ${JSON.stringify(
          config,
        )}`,
      );
    }
    return config;
  }

  private getEffectiveAuthConfig(defaultVersion: AuthVersionsConfig) {
    if (
      this.appReadyFor("employee", "v2") &&
      defaultVersion.employee.version === "v2"
    ) {
      return authBiModeConfig.employeev2;
    }
    if (
      this.appReadyFor("citizen", "v2") &&
      defaultVersion.citizen.version === "v2"
    ) {
      return authBiModeConfig.citizenv2;
    }
    if (
      this.appReadyFor("employee", "v1") &&
      defaultVersion.employee.version === "v1"
    ) {
      return authBiModeConfig.employeev1;
    }
    return authBiModeConfig.citizenv1;
  }

  private appReadyFor(auth: "citizen" | "employee", version: "v1" | "v2") {
    return !!authBiModeConfig?.[`${auth}${version}`];
  }

  /**
   * Sets the correct authentification version in storage. Handles 'auth' flag
   * in the url. Clears window storage if 'auth=' version is different from current.
   */
  async getDefaultAuthVersion(): Promise<AuthVersionsConfig | null> {
    const storageVersion = this.getAuthVersionFromStorage();
    const preferredAuthVersion = await this.getPreferredAuthVersion();
    const urlAuthVersion = this.getUrlAuthVersion();
    const versionSettedByUrlOrStorageOrPreffered =
      urlAuthVersion ?? storageVersion ?? preferredAuthVersion;

    if (!urlAuthVersion) {
      if (environment.oauthParameter.showDebugInformation) {
        console.debug(
          `Not setting authVersion in localStorage as no auth switch was passed in url`,
        );
      }
      return versionSettedByUrlOrStorageOrPreffered;
    }

    if (environment.oauthParameter.showDebugInformation) {
      console.debug(
        `${JSON.stringify({
          versionSettedByUrlOrStorage: versionSettedByUrlOrStorageOrPreffered,
          storageVersion,
        })}`,
      );
    }

    const mustSwitchVersion =
      versionSettedByUrlOrStorageOrPreffered.citizen.version !==
        storageVersion?.citizen?.version ||
      versionSettedByUrlOrStorageOrPreffered.employee.version !==
        storageVersion?.employee?.version;

    if (mustSwitchVersion) {
      // clear storage
      this.removeAccessToken();
      this.setAuthVersionFromStorage(versionSettedByUrlOrStorageOrPreffered);
      console.warn(`About to switch auth`);
      return versionSettedByUrlOrStorageOrPreffered;
    }

    if (environment.oauthParameter.showDebugInformation) {
      console.debug(
        `Not setting authVersion in localStorage as no auth switch was required`,
      );
    }

    return versionSettedByUrlOrStorageOrPreffered;
  }

  public removeAccessToken() {
    // no cleaner way to do this... :-(
    //
    // we want to avoid oauthService.revokeTokenAndLogout()
    // as it could break Single Sign-On.
    localStorage.removeItem("access_token");
    // avoid having access_token resurrected...
    localStorage.removeItem("refresh_token");
  }

  getUrlAuthVersion(): AuthVersionsConfig | null {
    if (environment.oauthParameter.showDebugInformation) {
      console.debug(`document.location.search=${document.location.search}`);
    }
    const urlVersion = new URLSearchParams(document.location.search).get(
      "auth",
    );
    return urlVersion
      ? {
          citizen: { version: urlVersion },
          employee: { version: urlVersion },
        }
      : null;
  }

  async getPreferredAuthVersion(): Promise<AuthVersionsConfig> {
    return {
      citizen: { version: "v1" },
      employee: { version: "v2" },
    };
  }
}
